import React, { useEffect, useState } from "react";
import axios from "axios";

export default function AssignSessionForm() {
  const [backendUrl, setBackendUrl] = useState("");
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);

  const [formData, setFormData] = useState({
    course_id: "",
    instructor_id: "",
    start_date: "",
    end_date: "",
    session_name: "",
    location: ""
  });

  // ---------- Load backend URL ----------
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "/config.js";

    script.onload = () => {
      if (window.env?.BACKEND_URL) {
        setBackendUrl(window.env.BACKEND_URL);
      } else {
        console.error("❌ BACKEND_URL not found in config.js");
      }
    };

    script.onerror = () =>
      console.error("❌ Failed to load config.js");

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // ---------- Fetch Courses & Instructors ----------
  useEffect(() => {
    if (!backendUrl) return;

    const fetchData = async () => {
      try {
        const [courseRes, instructorRes] = await Promise.all([
          axios.get(`${backendUrl}/courses`),
          axios.get(`${backendUrl}/users?role=Instructor`)
        ]);

        setCourses(courseRes.data || []);
        setInstructors(instructorRes.data || []);

      } catch (err) {
        console.error("❌ Failed to load courses/instructors:", err);
        alert("Failed to load courses or instructors.");
      }
    };

    fetchData();
  }, [backendUrl]);

  // ---------- Handle Change ----------
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // ---------- Create & Assign Session ----------
  const handleAssignSession = async (e) => {
    e.preventDefault();

    if (!backendUrl) return;

    if (
      !formData.course_id ||
      !formData.instructor_id ||
      !formData.start_date ||
      !formData.end_date
    ) {
      alert(
        "⚠️ Course, Instructor, Start Date, and End Date are required."
      );
      return;
    }

    try {
      await axios.post(
        `${backendUrl}/api/admin/assign-session`,
        {
          course_id: Number(formData.course_id),
          instructor_id: Number(formData.instructor_id),
          start_date: formData.start_date,
          end_date: formData.end_date,
          session_name: formData.session_name,
          location: formData.location
        }
      );

      alert("✅ Session assigned successfully");

      setFormData({
        course_id: "",
        instructor_id: "",
        start_date: "",
        end_date: "",
        session_name: "",
        location: ""
      });

    } catch (err) {
      console.error("❌ Session assignment error:", err);

      alert(
        err.response?.data?.message ||
        "Failed to assign session"
      );
    }
  };

  // ---------- Styles ----------
  const styles = {
    container: {
      backgroundColor: "#ffffff",
      color: "#6E6F70",
      padding: "40px",
      maxWidth: "600px",
      margin: "40px auto",
      borderRadius: "8px",
      boxShadow: "0 0 10px rgba(110, 111, 112, 0.1)",
      fontFamily: "Roboto, Arial, sans-serif",
    },

    title: {
      fontSize: "1.3rem",
      color: "#E30016",
      marginBottom: "30px",
      textAlign: "center",
    },

    form: {
      display: "flex",
      flexDirection: "column",
      gap: "20px",
    },

    formGroup: {
      display: "flex",
      flexDirection: "column",
      textAlign: "left",
    },

    label: {
      marginBottom: "6px",
      fontWeight: "500",
    },

    input: {
      padding: "10px",
      border: "1px solid #ccc",
      borderRadius: "5px",
      fontSize: "1rem",
      color: "#6E6F70",
      backgroundColor: "#fff",
    },

    select: {
      padding: "10px",
      border: "1px solid #ccc",
      borderRadius: "5px",
      fontSize: "1rem",
      color: "#6E6F70",
      backgroundColor: "#fff",
    },

    button: {
      backgroundColor: "#E30016",
      color: "white",
      border: "none",
      padding: "12px 24px",
      borderRadius: "5px",
      fontSize: "1rem",
      cursor: "pointer",
      alignSelf: "center",
      transition: "background-color 0.3s",
    },
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Create Course Session</h2>

      <form
        onSubmit={handleAssignSession}
        style={styles.form}
      >

        {/* Course */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Course:</label>

          <select
            name="course_id"
            value={formData.course_id}
            onChange={handleChange}
            required
            style={styles.select}
          >
            <option value="">-- Select Course --</option>

            {courses.map((c) => (
              <option
                key={c.course_id}
                value={c.course_id}
              >
                {c.course_name}
              </option>
            ))}
          </select>
        </div>

        {/* Instructor */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Instructor:</label>

          <select
            name="instructor_id"
            value={formData.instructor_id}
            onChange={handleChange}
            required
            style={styles.select}
          >
            <option value="">-- Select Instructor --</option>

            {instructors.map((i) => (
              <option
                key={i.User_ID}
                value={i.User_ID}
              >
                {i.Username}
              </option>
            ))}
          </select>
        </div>

        {/* Start Date */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Start Date:</label>

          <input
            type="date"
            name="start_date"
            value={formData.start_date}
            onChange={handleChange}
            required
            style={styles.input}
          />
        </div>

        {/* End Date */}
        <div style={styles.formGroup}>
          <label style={styles.label}>End Date:</label>

          <input
            type="date"
            name="end_date"
            value={formData.end_date}
            onChange={handleChange}
            required
            style={styles.input}
          />
        </div>

        {/* Session Name */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Session Name:</label>

          <input
            type="text"
            name="session_name"
            value={formData.session_name}
            onChange={handleChange}
            style={styles.input}
          />
        </div>

        {/* Location */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Location:</label>

          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            style={styles.input}
          />
        </div>

        <button
          type="submit"
          style={styles.button}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = "#b80012")
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = "#E30016")
          }
        >
          Assign Session
        </button>

      </form>
    </div>
  );
}